import React from 'react';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Repeat } from 'lucide-react';
import { WEEKDAYS, NTH_OPTIONS, RECURRENCE_TYPES } from '../../lib/recurrence';

const selectCls = 'bg-black/40 border-white/10 text-white h-10';
const contentCls = 'bg-[#0D0D0D] border-white/10 text-white';

export const RecurrenceFields = ({ value, onChange }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  const toggleDay = (d) => {
    const has = value.weekdays.includes(d);
    set({ weekdays: has ? value.weekdays.filter((x) => x !== d) : [...value.weekdays, d].sort() });
  };

  return (
    <div className="space-y-3" data-testid="event-form-recurrence">
      <div>
        <Label className="text-gray-300 text-xs uppercase tracking-wider flex items-center gap-1.5">
          <Repeat className="w-3.5 h-3.5" /> Repeats
        </Label>
        <Select value={value.type} onValueChange={(v) => set({ type: v })}>
          <SelectTrigger className={selectCls} data-testid="event-form-recurrence-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={contentCls}>
            {RECURRENCE_TYPES.map((o) => (
              <SelectItem key={o.value} value={o.value} data-testid={`event-form-recurrence-${o.value}`}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {value.type === 'weekly' && (
        <div>
          <Label className="text-gray-300 text-xs uppercase tracking-wider">On these days</Label>
          <div className="flex gap-1.5 mt-1.5 flex-wrap" data-testid="event-form-weekdays">
            {WEEKDAYS.map((d) => {
              const on = value.weekdays.includes(d.value);
              return (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => toggleDay(d.value)}
                  className={`w-11 h-9 rounded-full text-xs font-bold transition-colors border ${
                    on
                      ? 'bg-[#FF007F] border-[#FF007F] text-white'
                      : 'bg-white/5 border-white/10 text-gray-400 hover:text-white hover:border-white/30'
                  }`}
                  aria-pressed={on}
                  data-testid={`event-form-weekday-${d.value}`}
                >
                  {d.short}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {value.type === 'monthly' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-gray-300 text-xs uppercase tracking-wider">Which</Label>
            <Select value={String(value.nth)} onValueChange={(v) => set({ nth: Number(v) })}>
              <SelectTrigger className={selectCls} data-testid="event-form-monthly-nth"><SelectValue /></SelectTrigger>
              <SelectContent className={contentCls}>
                {NTH_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-gray-300 text-xs uppercase tracking-wider">Weekday</Label>
            <Select value={String(value.weekday)} onValueChange={(v) => set({ weekday: Number(v) })}>
              <SelectTrigger className={selectCls} data-testid="event-form-monthly-weekday"><SelectValue /></SelectTrigger>
              <SelectContent className={contentCls}>
                {WEEKDAYS.map((d) => (
                  <SelectItem key={d.value} value={String(d.value)}>{d.long}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {value.type !== 'none' && (
        <p className="text-gray-500 text-[11px]">
          Runs every occurrence until you pause or archive it. Guests RSVP for a specific date.
        </p>
      )}
    </div>
  );
};
